import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { TokenStatusIndicator } from './components/auth/TokenStatusIndicator';
import { ToastProvider } from './contexts/ToastContext';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { logger } from './lib/logger';

// Lazy load all page components for better code splitting
// Note: Pages use named exports, so we need to destructure and return as default
const LoginPage = lazy(() => {
  logger.debug('App', 'Loading LoginPage...');
  return import('./features/auth/LoginPage').then(module => {
    logger.debug('App', 'LoginPage loaded ✅');
    return { default: module.LoginPage };
  });
});

const DashboardPage = lazy(() => {
  logger.debug('App', 'Loading DashboardPage...');
  return import('./pages/DashboardPage').then(module => {
    logger.debug('App', 'DashboardPage loaded ✅');
    return { default: module.DashboardPage };
  });
});

const NotesPage = lazy(() => {
  logger.debug('App', 'Loading NotesPage...');
  return import('./pages/NotesPage').then(module => {
    logger.debug('App', 'NotesPage loaded ✅');
    return { default: module.NotesPage };
  });
});

const CalendarPage = lazy(() => {
  logger.debug('App', 'Loading CalendarPage...');
  return import('./pages/CalendarPage').then(module => {
    logger.debug('App', 'CalendarPage loaded ✅');
    return { default: module.CalendarPage };
  });
});

const SpacesPage = lazy(() => {
  logger.debug('App', 'Loading SpacesPage...');
  return import('./pages/SpacesPage').then(module => {
    logger.debug('App', 'SpacesPage loaded ✅');
    return { default: module.SpacesPage };
  });
});

const SettingsPage = lazy(() => {
  logger.debug('App', 'Loading SettingsPage...');
  return import('./pages/SettingsPage').then(module => {
    logger.debug('App', 'SettingsPage loaded ✅');
    return { default: module.SettingsPage };
  });
});

const AdminFeedbackPage = lazy(() => {
  logger.debug('App', 'Loading AdminFeedbackPage...');
  return import('./pages/AdminFeedbackPage').then(module => {
    logger.debug('App', 'AdminFeedbackPage loaded ✅');
    return { default: module.AdminFeedbackPage };
  });
});

const AdminAlphaInvitePage = lazy(() => {
  logger.debug('App', 'Loading AdminAlphaInvitePage...');
  return import('./pages/AdminAlphaInvitePage').then(module => {
    logger.debug('App', 'AdminAlphaInvitePage loaded ✅');
    return { default: module.AdminAlphaInvitePage };
  });
});

const AdminManagePage = lazy(() => {
  logger.debug('App', 'Loading AdminManagePage...');
  return import('./pages/AdminManagePage').then(module => {
    logger.debug('App', 'AdminManagePage loaded ✅');
    return { default: module.AdminManagePage };
  });
});

const DocsAlphaGuidePage = lazy(() => {
  logger.debug('App', 'Loading DocsAlphaGuidePage...');
  return import('./pages/DocsAlphaGuidePage').then(module => {
    logger.debug('App', 'DocsAlphaGuidePage loaded ✅');
    return { default: module.DocsAlphaGuidePage };
  });
});

const DocsFutureFeaturesPage = lazy(() => {
  logger.debug('App', 'Loading DocsFutureFeaturesPage...');
  return import('./pages/DocsFutureFeaturesPage').then(module => {
    logger.debug('App', 'DocsFutureFeaturesPage loaded ✅');
    return { default: module.DocsFutureFeaturesPage };
  });
});

const InviteAcceptPage = lazy(() => {
  logger.debug('App', 'Loading InviteAcceptPage...');
  return import('./pages/InviteAcceptPage').then(module => {
    logger.debug('App', 'InviteAcceptPage loaded ✅');
    return { default: module.InviteAcceptPage };
  });
});

function App() {
  return (
    <ErrorBoundary componentName="App">
      <ToastProvider>
        {/* Token status indicator - shows warnings when token is expiring/expired */}
        <TokenStatusIndicator />

        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
        <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingSpinner size="lg" fullScreen text="Loading..." />}>
              <LoginPage />
            </Suspense>
          }
        />

        {/* Documentation Routes (Public - no auth required) */}
        <Route
          path="/docs/alpha-guide"
          element={
            <Suspense fallback={<LoadingSpinner size="lg" fullScreen text="Loading documentation..." />}>
              <DocsAlphaGuidePage />
            </Suspense>
          }
        />
        <Route
          path="/docs/future-features"
          element={
            <Suspense fallback={<LoadingSpinner size="lg" fullScreen text="Loading documentation..." />}>
              <DocsFutureFeaturesPage />
            </Suspense>
          }
        />

        {/* Invitation Route (Public - handles auth internally) */}
        <Route
          path="/invite/:token"
          element={
            <Suspense fallback={<LoadingSpinner size="lg" fullScreen text="Loading invitation..." />}>
              <InviteAcceptPage />
            </Suspense>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner size="lg" text="Loading dashboard..." />}>
                  <DashboardPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notes"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary componentName="NotesPage">
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading notes..." />}>
                    <NotesPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary componentName="CalendarPage">
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading calendar..." />}>
                    <CalendarPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/spaces"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ErrorBoundary componentName="SpacesPage">
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading spaces..." />}>
                    <SpacesPage />
                  </Suspense>
                </ErrorBoundary>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner size="lg" text="Loading settings..." />}>
                  <SettingsPage />
                </Suspense>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/feedback"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <DashboardLayout>
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading admin feedback..." />}>
                    <AdminFeedbackPage />
                  </Suspense>
                </DashboardLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/alpha-testers"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <DashboardLayout>
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading alpha testers..." />}>
                    <AdminAlphaInvitePage />
                  </Suspense>
                </DashboardLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/manage-admins"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <DashboardLayout>
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading admin management..." />}>
                    <AdminManagePage />
                  </Suspense>
                </DashboardLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 - Redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </BrowserRouter>
    </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
